import { Translation } from 'cng-common/features/translations'
import { Flex } from 'core-components/library/flex'
import { IColumn } from 'core-components/library/list/types'
import { AbsolutePopup, TVisibilityTrigger } from 'core-components/library/popup'
import { PartnerNameWithLink } from 'partner360/components/partnerNameWithLink'
import { PartnerFlagRequestDetails } from 'partner360/features/topPartnersManagement/components/partnerFlagRequestHistoryCard/PartnerFlagRequestDetails'
import { PartnerFlags } from 'partner360/features/topPartnersManagement/components/partnerFlags/PartnerFlags'
import {
  formatMarketCoverage,
  getMarketCoverage,
} from 'partner360/features/topPartnersManagement/helpers/getMarketCoverage'
import { IPreparedTopPartner } from 'partner360/features/topPartnersManagement/models'
import { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'

import { TRANSLATION_KEYS } from '../../../translations'
import { StatusBadge } from '../../statusBadge/StatusBadge'
import styles from '../TopPartnersManagementTable.module.scss'

interface IGetColumns {
  currentYear?: number
  partnerFlags: IP360PartnerFlags[]
  topFlags: IP360PartnerFlags[]
}

export const getColumns = ({
  currentYear,
  partnerFlags,
  topFlags,
}: IGetColumns): IColumn<IPreparedTopPartner>[] => {
  const columns = [
    {
      field: 'GeoMarket',
      titleRender: () => {
        return (
          <div>
            <Translation text={TRANSLATION_KEYS.topPartners.table.market} />
            <Translation text={TRANSLATION_KEYS.topPartners.table.country} />
          </div>
        )
      },
      renderFunction: (item: IPreparedTopPartner) => {
        return (
          <Flex>
            <div>
              <div>{item.GeoMarket}</div>
              <div>{item.Country}</div>
            </div>
          </Flex>
        )
      },
      title: TRANSLATION_KEYS.topPartners.table.market,
      priority: 1,
    },
    {
      field: 'PartnerId',
      title: TRANSLATION_KEYS.topPartners.table.pid,
      priority: 2,
      titleRender: () => {
        return (
          <div>
            <Translation text={TRANSLATION_KEYS.topPartners.table.pid} />
            <Translation text={TRANSLATION_KEYS.topPartners.table.location} />
          </div>
        )
      },
      renderFunction: (item: IPreparedTopPartner) => {
        return (
          <div>
            <div>{item.PartnerId}</div>
            <div>{item.LocationId}</div>
          </div>
        )
      },
    },
    {
      field: 'PartnerName',
      title: TRANSLATION_KEYS.topPartners.table.partnerName,
      titleRender: () => <Translation text={TRANSLATION_KEYS.topPartners.table.partnerName} />,
      priority: 3,
      renderFunction: (item: IPreparedTopPartner) => {
        return (
          <PartnerNameWithLink
            partnerName={item.PartnerName}
            partnerId={item.PartnerId}
            fYear={currentYear}
          />
        )
      },
    },
    {
      field: 'Coverage',
      title: TRANSLATION_KEYS.topPartners.table.coverage,
      titleRender: () => <Translation text={TRANSLATION_KEYS.topPartners.table.coverage} />,
      priority: 4,
      renderFunction: (item: IPreparedTopPartner) => (
        <div>
          {topFlags.map((flag) => {
            const coverage = getMarketCoverage(item.Coverage, flag)
            return (
              <div key={flag.FlagKey}>
                {topFlags.length > 1 && `${flag.Title}: `}
                {coverage === null ? '-' : formatMarketCoverage(coverage)}
              </div>
            )
          })}
        </div>
      ),
    },
    {
      field: 'PbmEmail',
      title: TRANSLATION_KEYS.topPartners.table.pbm,
      titleRender: () => <Translation text={TRANSLATION_KEYS.topPartners.table.pbm} />,
      priority: 4,
    },
    {
      field: 'RequestStatus',
      title: TRANSLATION_KEYS.topPartners.table.requestStatus,
      titleRender: () => <Translation text={TRANSLATION_KEYS.topPartners.table.requestStatus} />,
      priority: 4,
      renderFunction: (item: IPreparedTopPartner) => {
        return (
          <AbsolutePopup
            rootClassName={styles.statusPopup}
            visibilityTrigger={TVisibilityTrigger.Hover}
            popupContent={() => (
              <PartnerFlagRequestDetails item={item} partnerFlags={partnerFlags} />
            )}
          >
            <StatusBadge status={item.RequestStatus} />
          </AbsolutePopup>
        )
      },
    },
    {
      field: 'PartnerRanks',
      title: TRANSLATION_KEYS.topPartners.table.partnerFlag,
      titleRender: () => <Translation text={TRANSLATION_KEYS.topPartners.table.partnerFlag} />,
      priority: 6,
      renderFunction: (item: IPreparedTopPartner) => {
        return (
          <PartnerFlags
            rankMask={item.BitClass}
            flagRequests={item.Request}
            requestStatus={item.RequestStatus}
            availableFlags={topFlags}
          />
        )
      },
    },
  ]

  return columns
}
